import React, { useCallback, useMemo } from 'react';
import { noop } from '@splunk/dashboard-utils';

import { _ } from '@splunk/ui-utils/i18n';
import { useIconRegistry, useFeatureFlags } from '@splunk/dashboard-context';

import { Gallery, type Item, type GalleryProps } from '../Gallery';
import { GallerySearch } from '../GallerySearch';
import { GalleryMessage } from '../GalleryMessage';
import { GalleryUpload } from '../GalleryUpload';
import IconGalleryItem from './IconGalleryItem';
import {
    useRegistryGallery,
    type ResourceRegistry,
    type RegistryItemMapper,
} from '../hooks/useRegistryGallery';

export interface IconItem {
    id: string;
    metaData: {
        name: string;
    };
}

interface IconGalleryProps {
    providerType?: string;
    onClick?: (itemUrl: Item['url']) => void;
    enableSearch?: boolean;
    setHasChildModal?: GalleryProps['setHasChildModal'];
}

export const IconGallery = ({
    providerType: initialProviderType,
    onClick = noop,
    enableSearch = true,
    setHasChildModal,
}: IconGalleryProps) => {
    const { enableIconUploads, enableIconDelete, enableGallerySharingWarning } =
        useFeatureFlags();
    const registry = useIconRegistry() as ResourceRegistry<IconItem>;
    const listIdsOptions = useMemo(() => ({ meta: ['name'] }), []);
    const mapIconItem: RegistryItemMapper<IconItem> = useCallback(
        (idObj) => ({
            url: `${idObj.providerType}://${idObj.id}`,
            name: idObj.metaData.name.split('.')[0],
        }),
        []
    );
    const {
        items: icons,
        loading,
        searching,
        error,
        warning,
        providerType,
        handleUpload,
        handleSearch,
        handleDelete,
    } = useRegistryGallery<IconItem>({
        registry,
        initialProviderType,
        missingRegistryMessage: _('No icon registry found'),
        missingProviderMessage: _('No icon providers found'),
        emptyWarningMessage: _('No icons. Please upload icons.'),
        emptyDeleteWarningMessage: _('No Icons. Please upload icons.'),
        emptySearchWarningMessage: _('No icons found.'),
        listIdsOptions,
        mapItem: mapIconItem,
    });

    const canUploadIcons = enableIconUploads && !!providerType;

    const handleItemClick = useCallback(
        (url: string) => onClick(url),
        [onClick]
    );

    const search = useMemo(
        () => <GallerySearch onSearch={handleSearch} />,
        [handleSearch]
    );

    const message = useMemo(
        () => <GalleryMessage error={error} warning={warning} />,
        [error, warning]
    );

    const upload = useMemo(
        () => (
            <GalleryUpload
                fileTypes=".svg"
                onUpload={handleUpload}
                enableGallerySharingWarning={enableGallerySharingWarning}
            />
        ),
        [handleUpload, enableGallerySharingWarning]
    );

    return (
        <Gallery
            testId="icon-gallery"
            isLoading={loading || searching}
            // Do not display search input if it is explicitly disabled, there is an error,
            // or icons have not been initialized (null)
            search={enableSearch && !error && icons ? search : null}
            message={message}
            items={icons}
            Item={IconGalleryItem}
            upload={error || !canUploadIcons ? null : upload}
            onItemClick={handleItemClick}
            onItemRemove={enableIconDelete ? handleDelete : undefined}
            setHasChildModal={setHasChildModal}
            galleryType="icon"
        />
    );
};
